import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import JobCodeSection from '../components/JobCodeSection';
import Modal from '../components/Modal';
import SignaturePad from '../components/SignaturePad';
import {
  fetchCustomerSignoff,
  updateCustomerSignoff,
  customerSignoffPdfUrl,
  type CustomerSignoffRow,
} from '../api/customerSignoffs';
import { fetchInstallationAppointment } from '../api/installationAppointments';
import {
  ACCEPTANCE_ITEMS,
  TRAINING_TOPICS,
  VERIFICATION_TASKS,
  emptyCustomerSignoffForm,
  normalizeCustomerSignoffForm,
  type CustomerSignoffFormData,
  type SignoffEquipmentRow,
} from '../data/customerSignoffForm';
import { generateCustomerSignoffPdf } from '../utils/customerSignoffPdf';
import './Page.css';
import './Elevators.css';
import './Installation.css';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const CustomerSignOffDetail: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();

  const [signoff, setSignoff] = useState<CustomerSignoffRow | null>(null);
  const [form, setForm] = useState<CustomerSignoffFormData>(emptyCustomerSignoffForm());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [busy, setBusy] = useState(false);
  /** The client-review pass runs on the same device after the employee finishes. */
  const [clientMode, setClientMode] = useState(false);

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const status = signoff?.status ?? 'Pending';
  const employeeDone = !!signoff?.employee_completed_at;
  const isPending = status === 'Pending';
  // Employee fills the whole form first; the client only fills their initials + section.
  const empEditable = isPending && !employeeDone && !clientMode;
  const cliEditable = isPending && employeeDone && clientMode;
  const fullyReadOnly = !isPending;

  const load = useCallback(async () => {
    if (!Number.isInteger(id) || id <= 0) {
      setLoadError('Invalid sign-off id.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchCustomerSignoff(id);
      setSignoff(data);
      let normalized = normalizeCustomerSignoffForm(data.form_data);

      // Seed the Equipment Delivered table + a little project info from the linked
      // installation appointment the first time the form is opened.
      const needsSeed = normalized.equipment.length === 0 && data.installation_appointment_id != null;
      if (needsSeed) {
        try {
          const appt = await fetchInstallationAppointment(data.installation_appointment_id as number);
          const equipment: SignoffEquipmentRow[] = (appt.products || []).map((p) => ({
            name: p.product_name,
            qtyExpected: String(p.quantity ?? 1),
            qtyReceived: '',
            condition: '',
            clientInitials: '',
          }));
          normalized = {
            ...normalized,
            equipment,
            project: {
              ...normalized.project,
              customerName: normalized.project.customerName || appt.business_name || data.business_name,
              siteName: normalized.project.siteName || appt.business_name || data.business_name,
              siteAddress: normalized.project.siteAddress || (appt.business_address ?? ''),
              deploymentDate:
                normalized.project.deploymentDate ||
                (appt.installation_at ? appt.installation_at.slice(0, 10) : ''),
              technicians:
                normalized.project.technicians || (appt.assignees || []).map((a) => a.username).join(', '),
            },
          };
          // Persist the seed so it survives a refresh.
          void updateCustomerSignoff(id, { form_data: normalized }).catch(() => {});
        } catch {
          /* seeding is best-effort */
        }
      }
      setForm(normalized);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load sign-off.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const scheduleSave = useCallback(
    (next: CustomerSignoffFormData) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        void updateCustomerSignoff(id, { form_data: next }).catch(() => {
          /* best-effort autosave */
        });
      }, 800);
    },
    [id]
  );

  /** Mutate the form with a draft copy, then schedule an autosave. */
  const update = useCallback(
    (mutator: (draft: CustomerSignoffFormData) => void) => {
      setForm((prev) => {
        const next = JSON.parse(JSON.stringify(prev)) as CustomerSignoffFormData;
        mutator(next);
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const flushSave = async (next: CustomerSignoffFormData) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await updateCustomerSignoff(id, { form_data: next });
  };

  // ── Employee: mark their section ready for the client ──
  const handleEmployeeComplete = async () => {
    setSubmitError('');
    const company = form.finalAcceptance.company;
    const missing: string[] = [];
    if (!company.name.trim()) missing.push('Company Representative name');
    if (!company.signature) missing.push('Company Representative signature');
    if (missing.length > 0) {
      setSubmitError(`Please complete the Company Representative section before finishing: ${missing.join(', ')}.`);
      return;
    }
    setBusy(true);
    try {
      await flushSave(form);
      const updated = await updateCustomerSignoff(id, {
        form_data: form,
        installer_name: company.name,
        installer_signature: company.signature,
        employee_completed: true,
      });
      setSignoff(updated);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to finish employee section.');
    } finally {
      setBusy(false);
    }
  };

  const handleReopenEmployeeSection = async () => {
    setBusy(true);
    try {
      const updated = await updateCustomerSignoff(id, { employee_completed: false });
      setSignoff(updated);
      setClientMode(false);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to reopen employee section.');
    } finally {
      setBusy(false);
    }
  };

  // ── Client: submit their initials + signature to complete the sign-off ──
  const handleClientSubmit = async () => {
    setSubmitError('');
    const client = form.finalAcceptance.client;
    const missing: string[] = [];
    if (!client.name.trim()) missing.push('Client Representative name');
    if (!client.signature) missing.push('Client Representative signature');
    if (!client.date.trim()) missing.push('Client Representative date');
    // Only require initials on rows the client actually received (Qty Received > 0).
    const wasReceived = (e: { qtyReceived: string }) => {
      const n = parseFloat(String(e.qtyReceived).trim());
      return Number.isFinite(n) && n > 0;
    };
    const missingInitials = form.equipment.filter((e) => wasReceived(e) && !e.clientInitials.trim()).length;
    if (missingInitials > 0) {
      missing.push(`initials on ${missingInitials} received equipment item${missingInitials > 1 ? 's' : ''}`);
    }
    if (missing.length > 0) {
      setSubmitError(`Please complete the following before submitting: ${missing.join(', ')}.`);
      return;
    }
    setBusy(true);
    try {
      await flushSave(form);
      const pdf = await generateCustomerSignoffPdf(form, { businessName: signoff?.business_name || '' });
      const updated = await updateCustomerSignoff(id, {
        form_data: form,
        client_name: client.name,
        client_signature: client.signature,
        status: 'Completed',
        pdf_base64: pdf,
      });
      setSignoff(updated);
      setClientMode(false);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to submit sign-off.');
    } finally {
      setBusy(false);
    }
  };

  const handleVoid = async () => {
    const reason = voidReason.trim();
    if (!reason) return;
    setBusy(true);
    try {
      const updated = await updateCustomerSignoff(id, { status: 'Void', void_reason: reason });
      setSignoff(updated);
      setVoidOpen(false);
      setVoidReason('');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to void sign-off.');
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    setBusy(true);
    try {
      const updated = await updateCustomerSignoff(id, { status: 'Pending' });
      setSignoff(updated);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to reopen sign-off.');
    } finally {
      setBusy(false);
    }
  };

  // Small field helpers -----------------------------------------------------
  const textInput = (value: string, onChange: (v: string) => void, disabled: boolean, opts?: { maxWidth?: number; type?: string; placeholder?: string }) => (
    <input
      type={opts?.type ?? 'text'}
      className="form-input"
      style={{ maxWidth: opts?.maxWidth }}
      value={value}
      placeholder={opts?.placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <PageHeader title="Customer Sign-Off" subtitle="Installation acceptance form" />
            <div className="page-content">
              <p className="page-subtitle">Loading sign-off…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const empDisabled = !empEditable;
  const cliDisabled = !cliEditable;

  return (
    <div className="page-container">
      <div className="page-layout">
        {/* Client review runs as a focused, kiosk-style view — hide the sidebar. */}
        {!clientMode ? <Sidebar /> : null}
        <main className="page-main">
          <PageHeader title="Customer Sign-Off" subtitle="Installation acceptance form" />
          <div className="page-content">
            {!clientMode ? (
              <button
                type="button"
                className="cancel-button"
                onClick={() => navigate('/installation?tab=sign-offs')}
                style={{ marginBottom: '1rem' }}
              >
                ← Back to Customer Sign-Offs
              </button>
            ) : null}

            {loadError ? (
              <p className="create-order-error" role="alert">{loadError}</p>
            ) : signoff ? (
              <>
                <div className="si-detail-header">
                  <div>
                    <h2 style={{ margin: '0 0 0.35rem' }}>{signoff.business_name}</h2>
                    <span className={`si-status-badge si-status-${status === 'Pending' ? 'Scheduled' : status}`}>
                      {status === 'Pending' ? (employeeDone ? 'Awaiting client review' : 'In progress') : status}
                    </span>
                  </div>
                  {!clientMode ? (
                    <div className="si-detail-actions">
                      {signoff.installation_appointment_id ? (
                        <button
                          type="button"
                          className="cancel-button"
                          onClick={() => navigate(`/installation/${signoff.installation_appointment_id}`)}
                        >
                          View installation
                        </button>
                      ) : null}
                      {signoff.has_pdf ? (
                        <a
                          className="cancel-button"
                          href={customerSignoffPdfUrl(signoff.id)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                        >
                          View PDF
                        </a>
                      ) : null}
                      {isPending ? (
                        <button type="button" className="cancel-button" disabled={busy} onClick={() => setVoidOpen(true)}>
                          Void
                        </button>
                      ) : (
                        <button type="button" className="cancel-button" disabled={busy} onClick={() => void handleReopen()}>
                          Reopen
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>

                {status === 'Void' && signoff.void_reason ? (
                  <p className="create-order-error">Voided: {signoff.void_reason}</p>
                ) : null}
                {status === 'Completed' ? (
                  <p className="elevators-confirm-hint">
                    Completed{signoff.completed_at ? ` on ${formatDateTime(signoff.completed_at)}` : ''}. This record is read-only.
                  </p>
                ) : null}

                {clientMode ? (
                  <p className="elevators-confirm-hint" style={{ background: '#eef6ff', borderColor: '#bcd7f5' }}>
                    Client review — please review the completed installation below, add your initials for each item of
                    equipment, confirm the acceptance checklist, and sign in the Client Representative section.
                  </p>
                ) : employeeDone && isPending ? (
                  <p className="elevators-confirm-hint">
                    Your section is complete. Hand this device to the client and tap <strong>Open for client review</strong>.
                  </p>
                ) : null}

                <JobCodeSection jobCode={signoff.job_code} clientId={signoff.client_id} />

                {/* ── Project Information ── */}
                <section className="si-form-section">
                  <h3 className="si-form-section-title">Project Information</h3>
                  <div className="signoff-grid">
                    <div className="si-field"><label className="si-field-label">Customer Name</label>{textInput(form.project.customerName, (v) => update((d) => { d.project.customerName = v; }), empDisabled)}</div>
                    <div className="si-field"><label className="si-field-label">Site Name</label>{textInput(form.project.siteName, (v) => update((d) => { d.project.siteName = v; }), empDisabled)}</div>
                    <div className="si-field"><label className="si-field-label">Site Address</label>{textInput(form.project.siteAddress, (v) => update((d) => { d.project.siteAddress = v; }), empDisabled)}</div>
                    <div className="si-field"><label className="si-field-label">Deployment Date</label>{textInput(form.project.deploymentDate, (v) => update((d) => { d.project.deploymentDate = v; }), empDisabled, { type: 'date' })}</div>
                    <div className="si-field"><label className="si-field-label">Project Manager</label>{textInput(form.project.projectManager, (v) => update((d) => { d.project.projectManager = v; }), empDisabled)}</div>
                    <div className="si-field"><label className="si-field-label">Robotics Technician(s)</label>{textInput(form.project.technicians, (v) => update((d) => { d.project.technicians = v; }), empDisabled)}</div>
                  </div>
                </section>

                {/* ── Equipment Delivered ── */}
                <section className="si-form-section">
                  <h3 className="si-form-section-title">Equipment Delivered</h3>
                  <p className="si-form-section-desc">Products and expected quantities come from the installation appointment.</p>
                  <div className="signoff-table-wrap">
                    <table className="signoff-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Qty Expected</th>
                          <th>Qty Received</th>
                          <th>Condition</th>
                          <th>Client Initials</th>
                          {empEditable ? <th aria-label="Remove" /> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {form.equipment.map((row, idx) => (
                          <tr key={idx}>
                            <td data-label="Product">
                              {textInput(row.name, (v) => update((d) => { d.equipment[idx].name = v; }), empDisabled)}
                            </td>
                            <td data-label="Qty Expected">
                              {textInput(row.qtyExpected, (v) => update((d) => { d.equipment[idx].qtyExpected = v; }), empDisabled, { maxWidth: 90 })}
                            </td>
                            <td data-label="Qty Received">
                              {textInput(row.qtyReceived, (v) => update((d) => { d.equipment[idx].qtyReceived = v; }), empDisabled, { maxWidth: 90 })}
                            </td>
                            <td data-label="Condition">
                              <div className="signoff-radio-row">
                                {['Good', 'Damaged'].map((c) => (
                                  <label key={c} className="signoff-radio">
                                    <input
                                      type="radio"
                                      name={`cond-${idx}`}
                                      checked={row.condition === c}
                                      disabled={empDisabled}
                                      onChange={() => update((d) => { d.equipment[idx].condition = c; })}
                                    />
                                    {c}
                                  </label>
                                ))}
                              </div>
                            </td>
                            <td data-label="Client Initials">
                              {textInput(row.clientInitials, (v) => update((d) => { d.equipment[idx].clientInitials = v; }), cliDisabled, { maxWidth: 90, placeholder: cliEditable ? 'Initials' : '' })}
                            </td>
                            {empEditable ? (
                              <td>
                                <button
                                  type="button"
                                  className="cancel-button"
                                  onClick={() => update((d) => { d.equipment.splice(idx, 1); })}
                                  aria-label="Remove row"
                                >
                                  ✕
                                </button>
                              </td>
                            ) : null}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {empEditable ? (
                    <button
                      type="button"
                      className="cancel-button"
                      style={{ marginTop: '0.5rem' }}
                      onClick={() => update((d) => { d.equipment.push({ name: '', qtyExpected: '', qtyReceived: '', condition: '', clientInitials: '' }); })}
                    >
                      + Add equipment
                    </button>
                  ) : null}
                </section>

                {/* ── Installation Verification ── */}
                <section className="si-form-section">
                  <h3 className="si-form-section-title">Installation Verification</h3>
                  <p className="si-form-section-desc">Please verify that the following items have been completed.</p>
                  {VERIFICATION_TASKS.map((task) => {
                    const opts = task.allowNa ? ['Yes', 'No', 'N/A'] : ['Yes', 'No'];
                    return (
                      <div key={task.id} className="signoff-check-line">
                        <span className="signoff-check-label">{task.label}</span>
                        <div className="signoff-radio-row">
                          {opts.map((o) => (
                            <label key={o} className="signoff-radio">
                              <input
                                type="radio"
                                name={`ver-${task.id}`}
                                checked={form.verification[task.id] === o}
                                disabled={empDisabled}
                                onChange={() => update((d) => { d.verification[task.id] = o; })}
                              />
                              {o}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </section>

                {/* ── Training Completion ── */}
                <section className="si-form-section">
                  <h3 className="si-form-section-title">Training Completion</h3>
                  <p className="si-form-section-desc">The following training has been completed with the client.</p>
                  <div className="signoff-table-wrap">
                    <table className="signoff-table">
                      <thead>
                        <tr><th>Training Topic</th><th>Completed</th><th>Trainee Initials</th></tr>
                      </thead>
                      <tbody>
                        {TRAINING_TOPICS.map((topic) => {
                          const entry = form.training.topics[topic.id] ?? { done: false, initials: '' };
                          return (
                            <tr key={topic.id}>
                              <td data-label="Training Topic">{topic.label}</td>
                              <td data-label="Completed">
                                <input
                                  type="checkbox"
                                  checked={!!entry.done}
                                  disabled={empDisabled}
                                  onChange={(e) => update((d) => {
                                    const t = d.training.topics[topic.id] ?? { done: false, initials: '' };
                                    t.done = e.target.checked;
                                    d.training.topics[topic.id] = t;
                                  })}
                                />
                              </td>
                              <td data-label="Trainee Initials">
                                {textInput(entry.initials, (v) => update((d) => {
                                  const t = d.training.topics[topic.id] ?? { done: false, initials: '' };
                                  t.initials = v;
                                  d.training.topics[topic.id] = t;
                                }), empDisabled, { maxWidth: 90 })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="signoff-grid" style={{ marginTop: '0.75rem' }}>
                    <div className="si-field"><label className="si-field-label">Training Conducted By</label>{textInput(form.training.conductedByName, (v) => update((d) => { d.training.conductedByName = v; }), empDisabled)}</div>
                    <div className="si-field"><label className="si-field-label">Date</label>{textInput(form.training.conductedByDate, (v) => update((d) => { d.training.conductedByDate = v; }), empDisabled, { type: 'date' })}</div>
                  </div>
                </section>

                {/* ── Client Acceptance Checklist ── */}
                <section className="si-form-section">
                  <h3 className="si-form-section-title">Client Acceptance Checklist</h3>
                  <p className="si-form-section-desc">Please confirm the following:</p>
                  {ACCEPTANCE_ITEMS.map((item) => (
                    <label key={item.id} className="signoff-accept-line">
                      <input
                        type="checkbox"
                        checked={!!form.acceptance[item.id]}
                        disabled={cliDisabled}
                        onChange={(e) => update((d) => { d.acceptance[item.id] = e.target.checked; })}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </section>

                {/* ── Outstanding Items / Punch List ── */}
                <section className="si-form-section">
                  <h3 className="si-form-section-title">Outstanding Items / Punch List</h3>
                  <div className="signoff-table-wrap">
                    <table className="signoff-table">
                      <thead>
                        <tr>
                          <th>Item</th><th>Responsible Party</th><th>Target Completion Date</th>
                          {empEditable ? <th aria-label="Remove" /> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {form.punchList.length === 0 ? (
                          <tr><td colSpan={empEditable ? 4 : 3} style={{ color: '#6b7280' }}>None.</td></tr>
                        ) : form.punchList.map((row, idx) => (
                          <tr key={idx}>
                            <td data-label="Item">{textInput(row.item, (v) => update((d) => { d.punchList[idx].item = v; }), empDisabled)}</td>
                            <td data-label="Responsible Party">{textInput(row.responsibleParty, (v) => update((d) => { d.punchList[idx].responsibleParty = v; }), empDisabled)}</td>
                            <td data-label="Target Completion Date">{textInput(row.targetDate, (v) => update((d) => { d.punchList[idx].targetDate = v; }), empDisabled, { type: 'date' })}</td>
                            {empEditable ? (
                              <td>
                                <button type="button" className="cancel-button" aria-label="Remove row" onClick={() => update((d) => { d.punchList.splice(idx, 1); })}>✕</button>
                              </td>
                            ) : null}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {empEditable ? (
                    <button type="button" className="cancel-button" style={{ marginTop: '0.5rem' }} onClick={() => update((d) => { d.punchList.push({ item: '', responsibleParty: '', targetDate: '' }); })}>
                      + Add item
                    </button>
                  ) : null}
                </section>

                {/* ── Client Comments ── */}
                <section className="si-form-section">
                  <h3 className="si-form-section-title">Client Comments</h3>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={form.clientComments}
                    disabled={cliDisabled}
                    onChange={(e) => update((d) => { d.clientComments = e.target.value; })}
                  />
                </section>

                {/* ── Final Acceptance ── */}
                <section className="si-form-section">
                  <h3 className="si-form-section-title">Final Acceptance</h3>
                  <p className="si-form-section-desc">
                    By signing below, the customer acknowledges receipt of the equipment listed above, confirms completion
                    of the installation and training (unless otherwise noted), and accepts responsibility for the equipment.
                  </p>

                  <div className="signoff-signature-grid">
                    <div className="signoff-signature-block">
                      <h4 className="signoff-rep-title">Company Representative</h4>
                      <div className="si-field"><label className="si-field-label">Name</label>{textInput(form.finalAcceptance.company.name, (v) => update((d) => { d.finalAcceptance.company.name = v; }), empDisabled, { maxWidth: 320 })}</div>
                      <div className="si-field"><label className="si-field-label">Title</label>{textInput(form.finalAcceptance.company.title, (v) => update((d) => { d.finalAcceptance.company.title = v; }), empDisabled, { maxWidth: 320 })}</div>
                      <div className="si-field"><label className="si-field-label">Date</label>{textInput(form.finalAcceptance.company.date, (v) => update((d) => { d.finalAcceptance.company.date = v; }), empDisabled, { type: 'date', maxWidth: 200 })}</div>
                      <div className="si-field">
                        <label className="si-field-label">Signature</label>
                        <SignaturePad
                          value={form.finalAcceptance.company.signature}
                          onChange={(v) => update((d) => { d.finalAcceptance.company.signature = v; })}
                          readOnly={empDisabled}
                          width={360}
                          height={140}
                          ariaLabel="Company representative signature"
                        />
                      </div>
                    </div>

                    <div className="signoff-signature-block">
                      <h4 className="signoff-rep-title">Client Representative</h4>
                      <div className="si-field"><label className="si-field-label">Name</label>{textInput(form.finalAcceptance.client.name, (v) => update((d) => { d.finalAcceptance.client.name = v; }), cliDisabled, { maxWidth: 320 })}</div>
                      <div className="si-field"><label className="si-field-label">Title</label>{textInput(form.finalAcceptance.client.title, (v) => update((d) => { d.finalAcceptance.client.title = v; }), cliDisabled, { maxWidth: 320 })}</div>
                      <div className="si-field"><label className="si-field-label">Date</label>{textInput(form.finalAcceptance.client.date, (v) => update((d) => { d.finalAcceptance.client.date = v; }), cliDisabled, { type: 'date', maxWidth: 200 })}</div>
                      <div className="si-field">
                        <label className="si-field-label">Signature</label>
                        <SignaturePad
                          value={form.finalAcceptance.client.signature}
                          onChange={(v) => update((d) => { d.finalAcceptance.client.signature = v; })}
                          readOnly={cliDisabled}
                          width={360}
                          height={140}
                          ariaLabel="Client representative signature"
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* ── Optional Follow-Up ── */}
                <section className="si-form-section">
                  <h3 className="si-form-section-title">Optional Follow-Up</h3>
                  <div className="si-field">
                    <label className="si-field-label">30-Day Follow-Up Scheduled</label>
                    <div className="signoff-radio-row">
                      {['Yes', 'No'].map((o) => (
                        <label key={o} className="signoff-radio">
                          <input
                            type="radio"
                            name="followup-30day"
                            checked={form.followUp.thirtyDayScheduled === o}
                            disabled={empDisabled}
                            onChange={() => update((d) => { d.followUp.thirtyDayScheduled = o; })}
                          />
                          {o}
                        </label>
                      ))}
                    </div>
                    {empEditable ? (
                      <p className="si-form-section-desc" style={{ margin: '0.35rem 0 0' }}>
                        If Yes, a follow-up task is created for the installer 30 days after the installation date.
                      </p>
                    ) : null}
                  </div>
                  <div className="signoff-grid">
                    <div className="si-field"><label className="si-field-label">Primary Support Contact</label>{textInput(form.followUp.supportContact, (v) => update((d) => { d.followUp.supportContact = v; }), empDisabled)}</div>
                    <div className="si-field"><label className="si-field-label">Support Phone</label>{textInput(form.followUp.supportPhone, (v) => update((d) => { d.followUp.supportPhone = v; }), empDisabled)}</div>
                    <div className="si-field">
                      <label className="si-field-label">Support Email</label>
                      {textInput(form.followUp.supportEmail, (v) => update((d) => { d.followUp.supportEmail = v; }), empDisabled, { type: 'email' })}
                    </div>
                  </div>
                </section>

                {submitError ? <p className="create-order-error" role="alert">{submitError}</p> : null}

                {/* ── Actions ── */}
                {empEditable ? (
                  <div className="modal-actions" style={{ padding: 0 }}>
                    <button type="button" className="update-button" disabled={busy} onClick={() => void handleEmployeeComplete()}>
                      {busy ? 'Saving…' : 'Finish my section →'}
                    </button>
                  </div>
                ) : null}

                {isPending && employeeDone && !clientMode ? (
                  <div className="modal-actions" style={{ padding: 0 }}>
                    <button type="button" className="cancel-button" disabled={busy} onClick={() => void handleReopenEmployeeSection()}>
                      Edit my section
                    </button>
                    <button type="button" className="update-button" disabled={busy} onClick={() => setClientMode(true)}>
                      Open for client review
                    </button>
                  </div>
                ) : null}

                {cliEditable ? (
                  <div className="modal-actions" style={{ padding: 0 }}>
                    <button type="button" className="cancel-button" disabled={busy} onClick={() => setClientMode(false)}>
                      Back
                    </button>
                    <button type="button" className="update-button" disabled={busy} onClick={() => void handleClientSubmit()}>
                      {busy ? 'Submitting…' : 'Submit sign-off'}
                    </button>
                  </div>
                ) : null}

                {fullyReadOnly ? (
                  <p className="page-subtitle" style={{ marginTop: '0.5rem' }}>
                    {signoff.installer_signed_at ? `Company representative signed ${formatDateTime(signoff.installer_signed_at)}. ` : ''}
                    {signoff.client_signed_at ? `Client signed ${formatDateTime(signoff.client_signed_at)}.` : ''}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        </main>
      </div>

      <Modal isOpen={voidOpen} onClose={() => setVoidOpen(false)} title="Void sign-off">
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label" htmlFor="signoff-void-reason">Reason</label>
            <textarea
              id="signoff-void-reason"
              className="form-input"
              rows={4}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={busy} onClick={() => setVoidOpen(false)}>Cancel</button>
          <button type="button" className="update-button" disabled={busy || !voidReason.trim()} onClick={() => void handleVoid()}>
            {busy ? 'Voiding…' : 'Void sign-off'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CustomerSignOffDetail;
